/**
 * grid-slider-autoplay.js
 *
 * Continuous (marquee) autoplay for `<grid-slider>` web components rendered by
 * `snippets/multicolumn.liquid`. The base `<grid-slider>` component lives in
 * theme.js / theme.dev.js — we intentionally don't modify those vendor files.
 * Instead we observe `grid-slider` elements that opt in via
 * `data-autoplay="true"` and convert them into a seamless infinite marquee.
 *
 * How it works:
 *  - We clone the existing `[data-grid-item]` slides once and append the
 *    clones to the inner slider track, so the visible content is duplicated
 *    end-to-end.
 *  - We swap the native horizontal-scroll layout for a flex track that we
 *    translate via a CSS keyframes animation (`ef-marquee-scroll`). The
 *    animation runs from `translateX(0)` to `translateX(-50%)` linearly, so
 *    when it ends the duplicated copy is exactly where the original started
 *    → no visible jump.
 *  - The animation duration is taken from `data-autoplay-duration` (ms),
 *    falling back to ~25s per loop.
 *  - We hide the slider's lazily-created prev/next arrows since they don't
 *    make sense for a marquee.
 *  - Pause conditions: hover, focus-within, touch, section off-screen,
 *    tab hidden, and `prefers-reduced-motion: reduce`.
 *
 * No-op outside browsers that ship `IntersectionObserver` and
 * `MutationObserver` (both supported in every browser we target).
 */
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!('IntersectionObserver' in window) || !('MutationObserver' in window)) return;

  var DEFAULT_DURATION_MS = 25000;
  var MIN_DURATION_MS = 5000;
  var INITIALIZED_FLAG = '__efMarqueeInitialized';
  var ENABLED_CLASS = 'ef-marquee--enabled';
  var PAUSED_CLASS = 'ef-marquee--paused';
  var TRACK_CLASS = 'ef-marquee__track';
  var CLONE_FLAG = 'efMarqueeClone';

  var reducedMotionQuery =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

  function prefersReducedMotion() {
    return Boolean(reducedMotionQuery && reducedMotionQuery.matches);
  }

  function clampDuration(rawValue) {
    var parsed = parseInt(rawValue, 10);
    if (!isFinite(parsed) || parsed <= 0) return DEFAULT_DURATION_MS;
    return Math.max(MIN_DURATION_MS, parsed);
  }

  function setupSlider(sliderElement) {
    if (!sliderElement || sliderElement[INITIALIZED_FLAG]) return;

    var track = sliderElement.querySelector('[data-grid-slider]');
    var slides = track ? track.querySelectorAll('[data-grid-item]') : [];
    if (!track || !slides.length) {
      // grid-slider may not have rendered its inner track yet; try again after
      // a microtask. The DOM MutationObserver below will retry on insertions.
      return;
    }

    sliderElement[INITIALIZED_FLAG] = true;

    var durationMs = clampDuration(sliderElement.getAttribute('data-autoplay-duration'));

    // Mark the host + track so the CSS keyframes target only this slider.
    sliderElement.classList.add(ENABLED_CLASS);
    track.classList.add(TRACK_CLASS);
    track.style.setProperty('--ef-marquee-duration', durationMs + 'ms');

    // Duplicate the slides once so the animation can wrap seamlessly. Cloned
    // nodes are marked + `aria-hidden` for accessibility / screen-readers.
    var fragment = document.createDocumentFragment();
    slides.forEach(function (slide) {
      var clone = slide.cloneNode(true);
      clone.dataset[CLONE_FLAG] = 'true';
      clone.setAttribute('aria-hidden', 'true');
      // Strip the focus-trapping attributes from inner anchors so the cloned
      // copies don't appear in the tab order.
      clone.querySelectorAll('a, button').forEach(function (el) {
        el.setAttribute('tabindex', '-1');
        el.setAttribute('aria-hidden', 'true');
      });
      fragment.appendChild(clone);
    });
    track.appendChild(fragment);

    // Hide the slider's lazily-created arrows — they don't fit a marquee.
    var hideArrows = function () {
      var arrows = sliderElement.querySelector('.slider__arrows');
      if (arrows) arrows.style.display = 'none';
    };
    hideArrows();
    var arrowObserver = new MutationObserver(hideArrows);
    arrowObserver.observe(sliderElement, {childList: true, subtree: true});

    // Hover-revealed "advance one item" arrow. Click pauses the CSS marquee
    // animation, JS-tweens the track by one item-slot leftward, then resumes
    // the animation from that new position via a negative animation-delay so
    // the seam stays continuous.
    var advanceBtn = document.createElement('button');
    advanceBtn.type = 'button';
    advanceBtn.className = 'ef-marquee__advance';
    advanceBtn.setAttribute('aria-label', 'Show next item');
    // Right-chevron SVG so we don't depend on theme icon snippets / fonts.
    advanceBtn.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';
    sliderElement.appendChild(advanceBtn);

    var isHoverPaused = false;
    var isFocusPaused = false;
    var isTouchPaused = false;
    var isInView = false;
    var isAdvancing = false;

    function shouldPause() {
      if (prefersReducedMotion()) return true;
      if (document.hidden) return true;
      if (!isInView) return true;
      if (isHoverPaused || isFocusPaused || isTouchPaused) return true;
      if (isAdvancing) return true;
      return false;
    }

    function applyPauseState() {
      sliderElement.classList.toggle(PAUSED_CLASS, shouldPause());
    }

    function readCurrentTranslateX() {
      var style = window.getComputedStyle(track);
      var transform = style.transform || style.webkitTransform || 'none';
      if (transform === 'none') return 0;
      // matrix(a, b, c, d, tx, ty) or matrix3d(...,tx,ty,tz,1)
      var match = transform.match(/matrix(?:3d)?\(([^)]+)\)/);
      if (!match) return 0;
      var parts = match[1].split(',').map(function (v) {
        return parseFloat(v.trim());
      });
      if (parts.length === 6) return parts[4]; // 2d matrix tx
      if (parts.length === 16) return parts[12]; // 3d matrix tx
      return 0;
    }

    function getItemSlotWidth() {
      var firstSlide = track.querySelector('[data-grid-item]');
      if (!firstSlide) return 0;
      var rect = firstSlide.getBoundingClientRect();
      var marginRight = parseFloat(window.getComputedStyle(firstSlide).marginRight) || 0;
      return rect.width + marginRight;
    }

    // Click handler: smoothly advance the track by one item-slot. We
    // disable the CSS @keyframes entirely for the duration of the click
    // so there's no fight between two transforms, then re-enable the
    // animation with a negative animation-delay equal to the current
    // progress so the loop resumes exactly where it landed.
    advanceBtn.addEventListener('click', function () {
      if (isAdvancing) return;
      var slot = getItemSlotWidth();
      if (!slot) return;

      isAdvancing = true;
      // Force-pause the keyframes animation while we drive the transform
      // ourselves. Without the .marquee--paused class the @keyframes
      // would keep running underneath and produce a visible jump.
      sliderElement.classList.add(PAUSED_CLASS);

      var currentTx = readCurrentTranslateX();
      var targetTx = currentTx - slot;

      // Detach from the CSS animation, lock to the current transform,
      // flush a layout, then transition to the target position.
      track.style.animation = 'none';
      track.style.transition = 'none';
      track.style.transform = 'translate3d(' + currentTx + 'px, 0, 0)';
      // eslint-disable-next-line no-unused-expressions
      track.offsetWidth; // force reflow
      track.style.transition = 'transform 420ms cubic-bezier(0.22, 1, 0.36, 1)';
      track.style.transform = 'translate3d(' + targetTx + 'px, 0, 0)';

      var finish = function () {
        track.removeEventListener('transitionend', finish);
        // Compute progress at the new position so the keyframes can
        // resume exactly there. The keyframes go from 0 → -50% of the
        // track's total width, so half the scrollWidth = one full loop.
        var halfWidth = track.scrollWidth / 2;
        var progress = 0;
        if (halfWidth > 0) {
          var normalizedTx = ((-targetTx) % halfWidth + halfWidth) % halfWidth;
          progress = normalizedTx / halfWidth;
        }
        // Clear our manual transform and re-enable the CSS animation
        // starting at `progress` via a negative animation-delay.
        track.style.transition = '';
        track.style.transform = '';
        track.style.animation = '';
        track.style.animationDelay = (-(progress * durationMs)) + 'ms';

        isAdvancing = false;
        applyPauseState(); // un-pause if no hover/focus is active
      };
      track.addEventListener('transitionend', finish);
      // Safety fallback in case transitionend doesn't fire (e.g. tab hidden).
      setTimeout(function () {
        if (isAdvancing) finish();
      }, 650);
    });

    sliderElement.addEventListener('mouseenter', function () {
      isHoverPaused = true;
      applyPauseState();
    });
    sliderElement.addEventListener('mouseleave', function () {
      isHoverPaused = false;
      applyPauseState();
    });
    sliderElement.addEventListener('focusin', function () {
      isFocusPaused = true;
      applyPauseState();
    });
    sliderElement.addEventListener('focusout', function () {
      isFocusPaused = false;
      applyPauseState();
    });
    sliderElement.addEventListener(
      'touchstart',
      function () {
        isTouchPaused = true;
        applyPauseState();
      },
      {passive: true}
    );
    sliderElement.addEventListener('touchend', function () {
      isTouchPaused = false;
      applyPauseState();
    });

    document.addEventListener('visibilitychange', applyPauseState);

    if (reducedMotionQuery) {
      var motionListener = applyPauseState;
      if (typeof reducedMotionQuery.addEventListener === 'function') {
        reducedMotionQuery.addEventListener('change', motionListener);
      } else if (typeof reducedMotionQuery.addListener === 'function') {
        reducedMotionQuery.addListener(motionListener);
      }
    }

    var inViewObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          isInView = entry.isIntersecting;
          applyPauseState();
        });
      },
      {threshold: 0.05}
    );
    inViewObserver.observe(sliderElement);

    // Initial state — paused until in view.
    applyPauseState();

    // Tear down when the host element leaves the DOM (theme editor reloads).
    var detachObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.removedNodes.forEach(function (node) {
          if (node === sliderElement || (node.contains && node.contains(sliderElement))) {
            arrowObserver.disconnect();
            inViewObserver.disconnect();
            detachObserver.disconnect();
          }
        });
      });
    });
    if (sliderElement.parentNode) {
      detachObserver.observe(sliderElement.parentNode, {childList: true, subtree: true});
    }
  }

  function scanSliders(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var sliders = scope.querySelectorAll('grid-slider[data-autoplay="true"]');
    sliders.forEach(setupSlider);
  }

  function tryInitAll() {
    scanSliders(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInitAll);
  } else {
    tryInitAll();
  }

  // `grid-slider` builds its inner `[data-grid-slider]` track lazily, so we
  // also retry on any DOM mutation that might introduce new sliders or fill
  // in their inner tracks.
  var domObserver = new MutationObserver(function () {
    tryInitAll();
  });
  domObserver.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
